/**
 * `/platform/ai` — "AI you actually own"
 *
 * The single buyer-facing page that pulls together what makes
 * ClauseArk's AI different: BYOK on the customer's KMS, citation-backed
 * redlines, audit-traceable agents, the public Contract Agents SDK,
 * multi-language review, OCR fallback, vertical packs, and in-house
 * transcription on the live negotiation copilot.
 *
 * Source of truth is `AI_POSITIONING_PAGE` in `lib/content.ts` — every
 * matrix row is enforced by `scripts/validate-positioning-truth.ts`
 * against the rest of the content module so we cannot claim something
 * ships here without it being reflected on a buyer-facing capability
 * list elsewhere on the site.
 */
import { Layout } from "@/components/layout";
import { SEOHead } from "@/components/seo-head";
import { CapabilityMatrix } from "@/components/capability-matrix";
import { AI_POSITIONING_PAGE, AI_POSITIONING_MATRIX } from "@/lib/content";
import { ArrowRight } from "lucide-react";
import { motion } from "framer-motion";
import { Link } from "wouter";

export default function PlatformAiPage() {
  const page = AI_POSITIONING_PAGE;
  return (
    <Layout>
      <SEOHead title={page.seo.title} description={page.seo.description} />

      {/* Hero */}
      <section
        className="pt-24 pb-20 md:pt-32 md:pb-24 border-b border-border/50 bg-slate-50 dark:bg-navy/30 relative overflow-hidden"
        data-testid="platform-ai-hero"
      >
        <div className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-cyan-500/50 to-transparent" />
        <div className="container mx-auto px-4 md:px-6 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="max-w-3xl"
          >
            <div className="text-sm font-semibold text-indigo-600 dark:text-indigo-400 mb-4 tracking-wider uppercase">
              {page.hero.eyebrow}
            </div>
            <h1
              className="text-display mb-6 text-navy dark:text-white"
              data-testid="platform-ai-hero-title"
            >
              {page.hero.title}
            </h1>
            <p className="text-body-lg text-slate-600 dark:text-slate-300 mb-8">
              {page.hero.subtitle}
            </p>
            <div className="flex flex-wrap items-center gap-3">
              <Link
                href={page.hero.primaryCta.path}
                className="inline-flex items-center justify-center rounded-full bg-indigo-600 px-6 py-3 text-sm font-semibold text-white shadow hover:bg-indigo-700 transition"
                data-testid="platform-ai-hero-primary-cta"
              >
                {page.hero.primaryCta.label}
                <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
              <a
                href={page.hero.secondaryCta.path}
                className="inline-flex items-center justify-center rounded-full border border-slate-300 dark:border-white/20 bg-white dark:bg-transparent px-6 py-3 text-sm font-semibold text-navy dark:text-white hover:bg-slate-100 dark:hover:bg-white/5 transition"
                data-testid="platform-ai-hero-secondary-cta"
              >
                {page.hero.secondaryCta.label}
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Principles */}
      <section
        id="principles"
        className="py-20 md:py-28"
        data-testid="platform-ai-principles"
      >
        <div className="container mx-auto px-4 md:px-6">
          <div className="max-w-3xl mb-12">
            <h2 className="text-h2 text-navy dark:text-white mb-3">
              {page.principles.headline}
            </h2>
            <p className="text-body text-slate-600 dark:text-slate-400">
              {page.principles.sub}
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {page.principles.items.map((p, i) => (
              <motion.div
                key={p.title}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.04 }}
                className="rounded-2xl border border-mist dark:border-white/10 bg-white dark:bg-navy/40 p-6 shadow-sm"
                data-testid={`platform-ai-principle-${i}`}
              >
                <h3 className="text-h4 text-navy dark:text-white mb-2">
                  {p.title}
                </h3>
                <p className="text-sm text-slate-600 dark:text-slate-400 leading-relaxed">
                  {p.body}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Matrix */}
      <section
        id={page.matrix.sectionId}
        className="py-16 md:py-24 bg-slate-50 dark:bg-navy/30 border-y border-border/50"
        data-testid="platform-ai-matrix-section"
      >
        <div className="container mx-auto px-4 md:px-6">
          <div className="max-w-3xl mb-10">
            <div className="text-sm font-semibold text-indigo-600 dark:text-indigo-400 mb-3 tracking-wider uppercase">
              {page.matrix.eyebrow}
            </div>
            <h2 className="text-h2 text-navy dark:text-white mb-3">
              {page.matrix.headline}
            </h2>
            <p className="text-body text-slate-600 dark:text-slate-400">
              {page.matrix.sub}
            </p>
          </div>
          <CapabilityMatrix
            matrix={AI_POSITIONING_MATRIX}
            legend={page.matrix.legend}
            unverifiedFootnote={page.matrix.unverifiedFootnote}
            sourcesFootnote={page.matrix.sourcesFootnote}
          />
        </div>
      </section>

      {/* Closing */}
      <section className="py-20 md:py-28" data-testid="platform-ai-closing">
        <div className="container mx-auto px-4 md:px-6">
          <div className="max-w-3xl">
            <h2 className="text-h2 text-navy dark:text-white mb-4">
              {page.closing.headline}
            </h2>
            <p className="text-body-lg text-slate-600 dark:text-slate-400 mb-8">
              {page.closing.body}
            </p>
            <div className="flex flex-wrap items-center gap-3">
              <Link
                href={page.closing.primary.path}
                className="inline-flex items-center justify-center rounded-full bg-indigo-600 px-6 py-3 text-sm font-semibold text-white shadow hover:bg-indigo-700 transition"
                data-testid="platform-ai-closing-primary-cta"
              >
                {page.closing.primary.label}
                <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
              <a
                href={page.closing.secondary.path}
                className="inline-flex items-center justify-center rounded-full border border-slate-300 dark:border-white/20 bg-white dark:bg-transparent px-6 py-3 text-sm font-semibold text-navy dark:text-white hover:bg-slate-100 dark:hover:bg-white/5 transition"
                data-testid="platform-ai-closing-secondary-cta"
              >
                {page.closing.secondary.label}
              </a>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
}
